import React, { useState, useRef, useEffect } from 'react';
import { Item } from '@/stores/canvasStore';
import { Play, Pause, Film, Clock, Maximize2, Volume2, VolumeX } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface VideoCardProps {
  item: Item;
}

interface VideoContent {
  videoUrl?: string;
  thumbnailUrl?: string;
  filename?: string;
  title?: string;
  duration?: number;
  aiDescription?: string;
  storagePath?: string;
  thumbnailStoragePath?: string;
  mimeType?: string;
  size?: number;
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function VideoCard({ item }: VideoCardProps) {
  const [isPlayingInline, setIsPlayingInline] = useState(false);
  const [isExpandedModal, setIsExpandedModal] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const content = item.content as VideoContent;
  const { videoUrl, thumbnailUrl, filename, title, duration } = content;
  const displayName = title || filename || 'Video';

  // Auto-hide controls after inactivity
  useEffect(() => {
    if (showControls && isPlayingInline) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2500);
    }
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [showControls, isPlayingInline]);

  const handlePlayInline = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (videoRef.current) {
      if (isPlayingInline) {
        videoRef.current.pause();
        setIsPlayingInline(false);
      } else {
        videoRef.current.play();
        setIsPlayingInline(true);
      }
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Pause inline video when expanding
    if (videoRef.current && isPlayingInline) {
      videoRef.current.pause();
      setIsPlayingInline(false);
    }
    setIsExpandedModal(true);
  };

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleVideoEnded = () => {
    setIsPlayingInline(false);
    setCurrentTime(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <>
      <div 
        className="w-full h-full rounded-lg overflow-hidden card-shadow bg-card relative group"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => !isPlayingInline && setShowControls(false)}
        title="" // Prevent any browser tooltips
      >
        {/* Video element (hidden when not playing, shown when playing) */}
        {videoUrl && (
          <video
            ref={videoRef}
            src={videoUrl}
            className={cn(
              "w-full h-full object-cover absolute inset-0",
              isPlayingInline ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            muted={isMuted}
            playsInline
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handlePlayInline}
            title="" // Prevent browser tooltip
          />
        )}

        {/* Thumbnail (shown when not playing) */}
        {!isPlayingInline && (
          <>
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt="" // Empty alt to prevent tooltip
                className="w-full h-full object-cover"
                draggable={false}
                title="" // Prevent browser tooltip
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <Film className="w-12 h-12 text-muted-foreground" />
              </div>
            )}
          </>
        )}

        {/* Play/Pause button overlay */}
        <div 
          className={cn(
            "absolute inset-0 flex items-center justify-center transition-colors cursor-pointer",
            isPlayingInline 
              ? (showControls ? "bg-black/20" : "bg-transparent") 
              : "bg-black/30 group-hover:bg-black/40"
          )}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handlePlayInline}
          title="" // Prevent browser tooltip
        >
          <div 
            className={cn(
              "w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg transition-all",
              isPlayingInline && !showControls ? "opacity-0" : "opacity-100",
              !isPlayingInline && "group-hover:scale-110"
            )}
          >
            {isPlayingInline ? (
              <Pause className="w-7 h-7 text-slate-900" fill="currentColor" />
            ) : (
              <Play className="w-7 h-7 text-slate-900 ml-1" fill="currentColor" />
            )}
          </div>
        </div>

        {/* Top-right controls: Expand and Mute */}
        <div 
          className={cn(
            "absolute top-2 right-2 flex gap-1.5 transition-opacity",
            (showControls || !isPlayingInline) ? "opacity-100" : "opacity-0"
          )}
        >
          {isPlayingInline && (
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleMuteToggle}
              className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
              title="" // Prevent browser tooltip
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
          )}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleExpandClick}
            className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
            title="" // Prevent browser tooltip
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar (when playing) */}
        {isPlayingInline && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
            <div 
              className="h-full bg-white/90 transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Video info bar (when not playing) */}
        {!isPlayingInline && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
            <div className="flex items-center justify-between text-white text-xs">
              <span className="truncate flex-1 mr-2" title="">{displayName}</span>
              <div className="flex items-center gap-2">
                {duration !== undefined && duration > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(duration)}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Expanded video player modal */}
      <Dialog open={isExpandedModal} onOpenChange={setIsExpandedModal}>
        <DialogContent 
          className="max-w-4xl p-0 bg-black border-none overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogTitle className="sr-only">Playing: {displayName}</DialogTitle>
          {videoUrl && (
            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full max-h-[80vh] object-contain"
              onMouseDown={(e) => e.stopPropagation()}
            >
              Your browser does not support the video tag.
            </video>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
